// components/ImagesMode/ImagesMode.js
import React, { useState, useEffect, useCallback } from 'react';
import { FullScreen } from 'react-full-screen';
import { useTimerControl } from '../SharedControls/useTimerControl';
import { TimerControls } from '../SharedControls/TimerControls';
import apiConfig from '../../../config/apiConfig';
import useTranslation from '../../../config/useTranslation';
import { getImage } from './indexedDBUtils';
import '../../../fire-theme.css';

function ImagesMode() {
  const BUFFER_SIZE = 100;
  const [images, setImages] = useState([]);
  const [nextPage, setNextPage] = useState(null);
  const { t } = useTranslation();

  // Initialize timer control with image-specific configuration
  const {
    timer,
    roundTimer,
    changeInterval,
    roundDuration,
    isActive,
    currentIndex,
    isControlWindow,
    isFullScreen,
    fullScreenHandle,
    useNewsImages,
    getNextItem,
    handleIntervalChange,
    handleRoundDurationChange,
    handleResetRound: baseHandleResetRound,
    toggleActive,
    openControlWindow,
    toggleFullScreen,
    handleToggleNewsImages,
  } = useTimerControl({
    defaultInterval: 35,
    defaultRoundDuration: 90,
    defaultIsActive: true,
    itemCount: images.length,
    onNextItem: () => {
      if (currentIndex >= images.length - BUFFER_SIZE / 2 && nextPage) {
        fetchImages(nextPage);
      }
    },
  });

  const fetchImages = useCallback((url, reset = false) => {
    // If url is not provided, construct from current toggle state
    if (!url) {
      const endpoint = useNewsImages
        ? `${apiConfig.baseUrl}images_mode/news-source-images-get/`
        : `${apiConfig.baseUrl}${apiConfig.endpoints.getImages}`;
      url = `${endpoint}?page_size=${BUFFER_SIZE}`;
    }

    console.log(`Fetching images from: ${url}`);
    fetch(url)
      .then(response => response.json())
      .then(data => {
        console.log(`Fetched images:`, data.results);
        setImages(prevImages => {
          const newImages = reset ? data.results : [...prevImages, ...data.results];
          console.log(`New images array:`, newImages);
          return newImages.slice(Math.max(newImages.length - BUFFER_SIZE, 0));
        });
        setNextPage(data.next);
        console.log(`Next page URL: ${data.next}`);
      })
      .catch(error => console.error('Error fetching images:', error));
  }, [useNewsImages]);

  // fetchManyImages function removed - now handled in Settings component

  // Custom reset handler that combines timer reset with image reset
  const handleResetRound = useCallback(() => {
    setImages([]);
    fetchImages(null, true); // Pass null to trigger dynamic endpoint construction
    baseHandleResetRound();
  }, [fetchImages, baseHandleResetRound]);

  // Initial image fetch
  useEffect(() => {
    fetchImages(null, true);
  }, [fetchImages]);

  // Refetch when toggle changes
  useEffect(() => {
    if (useNewsImages !== undefined) { // Check if initialized
      console.log(`News images toggle changed to: ${useNewsImages}`);
      setImages([]);
      fetchImages(null, true); // Pass null to trigger dynamic endpoint construction
    }
  }, [useNewsImages, fetchImages]);

  // Image display logic
  const displayImage = useCallback(async (imageUrl) => {
    console.log(`Displaying image: ${imageUrl}`);
    try {
      const cachedImage = await getImage(imageUrl);
      if (cachedImage) {
        return URL.createObjectURL(cachedImage);
      }
    } catch (error) {
      console.error('Error retrieving cached image:', error);
    }
    return imageUrl;
  }, []);

  useEffect(() => {
    if (images.length > 0 && currentIndex < images.length) {
      console.log(`Current index: ${currentIndex}, Total images: ${images.length}`);
      displayImage(images[currentIndex].image_file).then(src => {
        const imgElement = document.getElementById('currentImage');
        if (imgElement) {
          imgElement.src = src;
        }
      });
    }
  }, [currentIndex, images, displayImage]);

  // Render control buttons for fullscreen and control panel
  const renderControlButtons = () => (
    <div
      style={{
        position: 'fixed',
        top: 10,
        right: 10,
        zIndex: 500,
        transition: 'opacity 0.3s ease-in-out',
        opacity: isFullScreen ? 0 : 1,
      }}
      onMouseEnter={(e) => isFullScreen && (e.currentTarget.style.opacity = '1')}
      onMouseLeave={(e) => isFullScreen && (e.currentTarget.style.opacity = '0')}
    >
      <button
        onClick={openControlWindow}
        style={{
          marginBottom: '10px',
          display: 'block',
          padding: '10px',
          backgroundColor: '#4CAF50',
          color: 'white',
          border: 'none',
          borderRadius: '5px',
          cursor: 'pointer',
          width: '100%'
        }}
      >
        {t('openControlPanel')}
      </button>
      <button
        onClick={toggleFullScreen}
        style={{
          display: 'block',
          padding: '10px',
          backgroundColor: '#4CAF50',
          color: 'white',
          border: 'none',
          borderRadius: '5px',
          cursor: 'pointer',
          width: '100%'
        }}
      >
        {isFullScreen ? t('exitFullScreen') : t('enterFullScreen')}
      </button>
    </div>
  );

  return (
    <FullScreen handle={fullScreenHandle}>
      <div
        className={`images-mode ${isFullScreen ? 'fullscreen' : ''}`}
        style={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          height: '100vh',
          backgroundColor: isFullScreen ? '#000' : 'transparent',
          overflow: 'hidden'
        }}
      >
        {!isControlWindow && (
          <>
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: '100%',
              flex: 1,
              minHeight: 0,
              position: 'relative',
              marginBottom: -10
            }}>
              {images.length > 0 ? (
                <img
                  id="currentImage"
                  alt="Display"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                    display: 'block'
                  }}
                />
              ) : (
                <p>No images loaded</p>
              )}
            </div>
            {renderControlButtons()}
          </>
        )}
        <div style={{ 
          flexShrink: 0,
          marginTop: -10 
        }}> 
          <TimerControls
            timer={timer}
            roundTimer={roundTimer}
            changeInterval={changeInterval}
            roundDuration={roundDuration}
            isActive={isActive}
            handleRoundDurationChange={handleRoundDurationChange}
            getNextItem={getNextItem}
            handleIntervalChange={handleIntervalChange}
            toggleActive={toggleActive}
            handleResetRound={handleResetRound}
            isControlWindow={isControlWindow}
            isFullScreen={isFullScreen}
            useNewsImages={useNewsImages}
            handleToggleNewsImages={handleToggleNewsImages}
            style={{
              padding: isFullScreen ? '10px' : 0
            }}
          />
        </div>
      </div>
    </FullScreen>
  );
}

export default ImagesMode;